import theme from '@ui/configs/vuetify';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';

const {
  pink500, green500, orange500, blue500, brandPrimary,
} = theme.theme.themes.light.colors;

export const tabsData = (isAdminOn) => {
  const { authData, isDemographicSurveyReportVisible } = storeToRefs(useAppAuthData());
  const final = [];
  const noEditorWrap = authData.value.noEditorEnabled;

  const standardize = {
    label: 'Standardize',
    svg: 'standardize-icon',
    valueProp: 'Datapeople helps us standardize our workflow to ensure we draft complete, inclusive and brand-compliant content',
    primaryColor: pink500,
    secondaryColor: '#3196DF',
    options: [{
      cardLabel: 'Manage Templates',
      description: 'Add boilerplate content to standardize job descriptions',
      route: { name: 'templates' },
    },
    {
      cardLabel: 'Manage Policy Guide',
      description: 'Ensure compliance with your company\'s internal policies',
      route: { name: 'policy-guide' },
    },
    {
      cardLabel: 'Manage Style Guide',
      description: 'Ensure compliance with your brand standards',
      route: { name: 'style-guide' },
    },
    {
      cardLabel: 'Standardize Open Jobs',
      description: 'See which open jobs are incomplete and/or too long',
      route: { name: 'employer-brand-report' },
    },
    {
      cardLabel: 'Manage Flagship Jobs',
      description: 'View, add, and update your organization\'s best-in-class jobs',
      route: { name: 'flagship' },
      isNew: true,
    }],
  };

  if (isAdminOn) final.push(standardize);

  const write = {
    label: 'Write',
    svg: 'write-icon',
    valueProp: 'The Datapeople Editor helps us write jobs and recruiting content that are effective, market-calibrated and inclusive',
    primaryColor: green500,
    secondaryColor: '#3196DF',
    options: [
      {
        cardLabel: 'Write a Job Post',
        description: 'Write a new job post',
        route: { name: 'home', query: { 'new-draft': 'true' } },
        noEditorWrap,
      },
      {
        cardLabel: 'Edit a Job Post',
        description: 'Refine an existing job post',
        route: { name: 'draft', query: { owned: 'true' } },
        noEditorWrap,
      },
      {
        cardLabel: 'Start with Flagship',
        description: 'Use a best-in-class job as your starting point',
        route: { name: 'flagship' },
        isNew: true,
        noEditorWrap,
      },
    ],
  };

  final.push(write);

  const track = {
    label: 'Track',
    svg: 'track-icon',
    valueProp: 'Datapeople Insights helps us track our current job posts and requisitions for content and compliance and pipeline issues',
    primaryColor: orange500,
    secondaryColor: '#3196DF',
    options: [
      {
        cardLabel: 'Track Compliance',
        description: 'See which open jobs are missing key compliance content',
        route: { name: 'compliance-report' },
      },
      {
        cardLabel: 'Job Post Library',
        description: 'View your company\'s past and present job posts',
        route: { name: 'published' },
      },
      {
        cardLabel: 'Flagship Library',
        description: 'View your organization\'s best-in-class job posts',
        route: { name: 'flagship' },
        isNew: true,
      },
      {
        cardLabel: 'Track Job Content',
        description: 'See a bird\'s-eye view of your jobs\' content',
        route: { name: 'content-snapshot' },
      },
    ],
  };

  if (authData.value.isApplicationReportingEnabled) {
    track.options.push({
      cardLabel: 'Req Progress Report Library',
      description: 'View the hiring progress of your requisitions.',
      route: { name: 'open-requisitions' },
    });
  }

  if (isDemographicSurveyReportVisible.value && authData.value.isApplicationReportingEnabled) {
    track.options.push({
      cardLabel: 'Demographic Survey Distribution',
      description: 'Review your survey reach',
      route: { name: 'demographic-survey-report', query: { tab: 'distribution' } },
    });
  }

  final.push(track);

  const analyze = {
    label: 'Analyze',
    svg: 'analyze-icon',
    valueProp: 'Datapeople Insights helps us analyze our hiring processes and candidate pipeline for efficiency or diversity bottlenecks',
    primaryColor: blue500,
    secondaryColor: brandPrimary,
    options: [],
  };

  analyze.options.push({
    cardLabel: 'Analyze Recruiting Performance',
    description: 'Start here to get a pulse on top recruiting metrics',
    route: { name: 'executive-insights' },
  });

  analyze.options.push({
    cardLabel: 'Analyze Funnel Diversity',
    description: 'View gender diversity at every stage of the hiring process',
    route: { name: 'gender-report' },
  });

  analyze.options.push({
    cardLabel: 'Analyze Hiring Outcomes',
    description: 'Understand your hiring efforts, from offer acceptance to time to hire',
    route: { name: 'hiring-report' },
  });

  if (isDemographicSurveyReportVisible.value) {
    analyze.options.push({
      cardLabel: 'Analyze Demographic Survey',
      description: 'Explore demographic trends based on candidate-reported data',
      route: { name: 'demographic-survey-report' },
    });
  }

  if (authData.value.isApplicationReportingEnabled) final.push(analyze);

  return final;
};
